import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Crown, Flame, TrendingUp, ScrollText, LogOut, MapPin } from "lucide-react";
import SovereignAnchor from "../components/SovereignAnchor";
import {
    AreaChart,
    Area,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    CartesianGrid,
} from "recharts";

const currency = (n) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

function Stat({ label, value, accent }) {
    return (
        <div className="glass rounded-xl p-5 border border-[hsl(40_40%_25%/0.25)]">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">{label}</div>
            <div className={`font-heading text-3xl mt-2 tracking-tight ${accent ? "gold-gradient-text" : ""}`}>{value}</div>
        </div>
    );
}

export default function Privateer() {
    const { logout } = useAuth();
    const [data, setData] = useState(null);
    const [err, setErr] = useState("");

    useEffect(() => {
        (async () => {
            try {
                const res = await api.get("/privateer/dashboard");
                setData(res.data);
            } catch (e) {
                setErr(e.response?.data?.detail || "Unable to load Privateer Bridge");
            }
        })();
    }, []);

    if (err) {
        return (
            <div className="min-h-screen bg-background text-foreground p-10">
                <div className="glass rounded-xl p-6 max-w-lg" data-testid="privateer-error">
                    <div className="text-sm text-[hsl(350_80%_70%)]">{err}</div>
                </div>
            </div>
        );
    }

    if (!data) {
        return <div className="min-h-screen bg-background text-muted-foreground p-10">Unfurling the Privateer ledger...</div>;
    }

    const monthly = (data.monthly || []).map((m) => ({
        month: m.month,
        fuel: m.ad_spend,
        velocity: m.net_revenue,
    }));

    return (
        <div className="min-h-screen bg-background text-foreground grain-overlay relative" data-testid="privateer-view">
            <div className="absolute inset-0 pointer-events-none opacity-40">
                <div className="absolute top-[-20%] right-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(40_60%_45%/0.12)] blur-[120px]" />
                <div className="absolute bottom-[-20%] left-[-10%] w-[55%] h-[55%] rounded-full bg-[hsl(153_50%_25%/0.22)] blur-[120px]" />
            </div>

            <header className="relative border-b border-[hsl(40_40%_25%/0.25)] bg-[hsl(151_55%_7%/0.7)] backdrop-blur-md">
                <div className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-4">
                    <div className="flex items-center gap-3">
                        <SovereignAnchor size={26} strokeWidth={6} className="text-[hsl(45_85%_68%)]" />
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">Founding Privateer Bridge</div>
                            <div className="font-heading text-2xl gold-gradient-text tracking-tight">EIGOO · Privateer Pact</div>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 text-sm">
                        <Link to="/app/map" className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors" data-testid="privateer-map-link">Territorial Map</Link>
                        <Link to="/app/dashboard" className="text-muted-foreground hover:text-[hsl(45_85%_68%)] transition-colors" data-testid="privateer-dashboard-link">Full Dashboard</Link>
                        <button
                            onClick={logout}
                            data-testid="privateer-logout"
                            className="flex items-center gap-1.5 text-muted-foreground hover:text-[hsl(350_80%_70%)] transition-colors"
                        >
                            <LogOut className="w-4 h-4" /> Disembark
                        </button>
                    </div>
                </div>
            </header>

            <main className="relative max-w-7xl mx-auto px-6 py-10 space-y-10">
                <section className="glass rounded-2xl p-8 md:p-10 border border-[hsl(40_40%_25%/0.3)]" data-testid="privateer-hero">
                    <div className="flex items-start justify-between flex-wrap gap-4">
                        <div>
                            <div className="text-[10px] uppercase tracking-[0.4em] text-muted-foreground">Founding Privateer</div>
                            <h1 className="font-heading text-5xl md:text-6xl mt-2 tracking-tighter" data-testid="privateer-name">
                                {data.privateer.name}
                            </h1>
                            <div className="mt-1 text-muted-foreground">
                                {data.privateer.agency_name} · <span className="font-mono text-xs">{data.privateer.email}</span>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">Founding Seal</div>
                            <div className="font-mono text-sm mt-1 text-[hsl(45_85%_68%)]" data-testid="privateer-seal">
                                #{data.pact.founding_seal_no}
                            </div>
                        </div>
                    </div>

                    <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Stat label="Fuel Burned" value={currency(data.totals.ad_spend)} />
                        <Stat label="Net Velocity" value={currency(data.totals.net_revenue)} accent />
                        <Stat label="Yield" value={currency(data.totals.yield)} />
                        <Stat label="Partner Share" value={`${Math.round(data.privateer.partner_share * 100)}%`} accent />
                    </div>
                </section>

                {/* Pact */}
                <section className="glass rounded-2xl p-6 md:p-8 border border-[hsl(40_40%_25%/0.3)]" data-testid="privateer-pact">
                    <div className="flex items-center gap-2 mb-4">
                        <ScrollText className="w-5 h-5 text-[hsl(45_85%_68%)]" />
                        <div className="font-heading text-2xl tracking-tight">The Privateer Pact</div>
                    </div>
                    <ul className="grid md:grid-cols-3 gap-4 text-sm">
                        <li className="rounded-lg border border-[hsl(40_40%_25%/0.3)] p-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(45_85%_68%)]">Charters</div>
                            <div className="mt-2 text-2xl font-heading">{data.pact.charters_granted} granted</div>
                            <div className="mt-1 text-muted-foreground text-xs">Three state territories, Director-matched to your sector.</div>
                        </li>
                        <li className="rounded-lg border border-[hsl(40_40%_25%/0.3)] p-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(45_85%_68%)]">Residual</div>
                            <div className="mt-2 text-2xl font-heading">Perpetuity</div>
                            <div className="mt-1 text-muted-foreground text-xs">Lifetime residual on all fleet revenue your charters generate.</div>
                        </li>
                        <li className="rounded-lg border border-[hsl(40_40%_25%/0.3)] p-4">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-[hsl(45_85%_68%)]">Boarding Fee</div>
                            <div className="mt-2 text-2xl font-heading">Waived</div>
                            <div className="mt-1 text-muted-foreground text-xs">Founding rank bypasses the standard boarding bond.</div>
                        </li>
                    </ul>
                </section>

                {monthly.length > 0 && (
                    <section className="glass rounded-2xl p-6 md:p-8 border border-[hsl(40_40%_25%/0.25)]">
                        <div className="flex items-center gap-2 mb-6">
                            <TrendingUp className="w-5 h-5 text-[hsl(45_85%_68%)]" />
                            <div className="font-heading text-2xl tracking-tight">Fuel vs Velocity</div>
                        </div>
                        <div className="h-72">
                            <ResponsiveContainer width="100%" height="100%">
                                <AreaChart data={monthly}>
                                    <defs>
                                        <linearGradient id="pFuel" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="hsl(40 61% 53%)" stopOpacity={0.6} />
                                            <stop offset="100%" stopColor="hsl(40 61% 53%)" stopOpacity={0} />
                                        </linearGradient>
                                        <linearGradient id="pVel" x1="0" y1="0" x2="0" y2="1">
                                            <stop offset="0%" stopColor="hsl(153 60% 55%)" stopOpacity={0.6} />
                                            <stop offset="100%" stopColor="hsl(153 60% 55%)" stopOpacity={0} />
                                        </linearGradient>
                                    </defs>
                                    <CartesianGrid strokeDasharray="3 3" stroke="hsl(40 20% 30% / 0.2)" />
                                    <XAxis dataKey="month" stroke="hsl(40 20% 60%)" fontSize={11} />
                                    <YAxis stroke="hsl(40 20% 60%)" fontSize={11} />
                                    <Tooltip
                                        contentStyle={{
                                            background: "hsl(151 55% 9%)",
                                            border: "1px solid hsl(40 40% 25% / 0.4)",
                                            borderRadius: 6,
                                        }}
                                    />
                                    <Area type="monotone" dataKey="fuel" stroke="hsl(40 61% 53%)" fill="url(#pFuel)" />
                                    <Area type="monotone" dataKey="velocity" stroke="hsl(153 60% 55%)" fill="url(#pVel)" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </div>
                    </section>
                )}

                <section className="grid md:grid-cols-2 gap-6">
                    <div className="glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.25)]">
                        <div className="flex items-center gap-2 mb-4">
                            <Flame className="w-5 h-5 text-[hsl(45_85%_68%)]" />
                            <div className="font-heading text-xl tracking-tight">Active Fuel Strikes</div>
                        </div>
                        {data.active_fuel?.length ? (
                            <ul className="space-y-3" data-testid="privateer-fuel-list">
                                {data.active_fuel.map((f) => (
                                    <li key={f.id} className="flex items-center justify-between text-sm border-b border-[hsl(40_40%_25%/0.15)] pb-2">
                                        <span className="uppercase tracking-[0.2em] text-[11px] text-[hsl(45_85%_68%)]">{f.tier}</span>
                                        <span className="font-mono">{currency(f.amount)}</span>
                                    </li>
                                ))}
                            </ul>
                        ) : (
                            <div className="text-sm text-muted-foreground">No active payloads yet.</div>
                        )}
                    </div>

                    <div className="glass rounded-2xl p-6 border border-[hsl(40_40%_25%/0.25)]">
                        <div className="flex items-center gap-2 mb-4">
                            <MapPin className="w-5 h-5 text-[hsl(45_85%_68%)]" />
                            <div className="font-heading text-xl tracking-tight">Claimed Charters</div>
                        </div>
                        {data.territories?.length ? (
                            <div className="flex flex-wrap gap-2" data-testid="privateer-territories">
                                {data.territories.map((t) => (
                                    <span
                                        key={t}
                                        className="px-3 py-1 rounded-full text-xs uppercase tracking-[0.2em] border border-[hsl(40_40%_35%/0.45)] text-[hsl(45_85%_68%)]"
                                    >
                                        {t}
                                    </span>
                                ))}
                            </div>
                        ) : (
                            <div className="text-sm text-muted-foreground">
                                No charters yet.{" "}
                                <Link to="/app/map" className="text-[hsl(45_85%_68%)] hover:underline">Select your three states →</Link>
                            </div>
                        )}
                    </div>
                </section>
            </main>
        </div>
    );
}
